import { CommonModule } from '@angular/common'
import { Component, inject, input } from '@angular/core'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { DialogModule } from 'primeng/dialog'
import { ImageModule } from 'primeng/image'
import { ProgressBarModule } from 'primeng/progressbar'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { SkeletonModule } from 'primeng/skeleton'
import { TooltipModule } from 'primeng/tooltip'

import {
  FlxButtonModule,
  FlxFormControlModule,
  FlxIconModule,
  FlxModalModule,
} from '@flowx/angular-ui-toolkit'
import { AiAnalystUtils } from '@flowx/designer/modules/new-ai/agents/analyst/services/analyst.service'
import { AiCoordinatorActions } from '@flowx/designer/modules/new-ai/agents/coordinator/store/coordinator.actions'
import { AiAgent } from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { FlxAiMarkdownPipe } from '@flowx/designer/modules/new-ai/common/pipes/flx-ai-markdown.pipe'
import { AiActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'

export enum CardStatus {
  DEFAULT = 'default',
  RUNNING = 'running',
  COMPLETED = 'completed',
}

export enum DestinationType {
  NODE = 'node',
  ACTION = 'action',
  UI = 'ui',
  DATAMODEL = 'datamodel',
}

export interface AgentPlan {
  title: string
  cards: AgentPlanCard[]
}

export interface AgentPlanCard {
  title: string
  description: string
  destinationType: DestinationType
  status: CardStatus
  type: string
}

@Component({
  selector: 'flx-ai-agent-plan',
  imports: [
    CommonModule,
    DialogModule,
    ImageModule,
    ProgressBarModule,
    TooltipModule,
    SkeletonModule,
    ProgressSpinnerModule,
    FlxButtonModule,
    FlxFormControlModule,
    FlxIconModule,
    FlxModalModule,
    FlxAiMarkdownPipe,
  ],
  templateUrl: './agent-plan.component.html',
  styleUrls: ['./agent-plan.component.scss'],
  standalone: true,
})
export class AgentPlanComponent {
  #store = inject(Store)
  #router = inject(Router)
  showTitle = input(true)

  selectedPlan = this.#store.selectSignal(AiSelectors.selectedPlan)
  generatingPlan = this.#store.selectSignal(AiSelectors.generatingPlan)
  currentStep = this.#store.selectSignal(AiSelectors.currentPlanStep)
  currentProcess = this.#store.selectSignal(AiSelectors.getProcessId)
  currentAppId = this.#store.selectSignal(AiSelectors.getAppId)
  streamingAiResponse = this.#store.selectSignal(AiSelectors.streamingAiResponse)
  hasDraft = this.#store.selectSignal(AiSelectors.hasDraft)
  hasBusinessProcess = this.#store.selectSignal(AiSelectors.hasBusinessProcess)
  showPromptModal = false
  descriptionExpanded = false
  currentPrompt = ''
  previewStep = null

  getIconByType(type: string): string {
    switch (type) {
      case 'node':
        return 'ph pt-1 text-yellow-500 ph-tree-structure font-bold'
      case 'action':
        return 'ph pt-1 text-yellow-500 ph-code font-bold'
      case 'ui':
        return 'ph pt-1 text-yellow-500 ph-layout font-bold'
      case 'datamodel':
        return 'ph pt-1 text-yellow-500 ph-database font-bold'
      default:
        return 'document'
    }
  }

  planName(): string {
    return this.selectedPlan()?.name || 'Execution Plan'
  }
  getStepBgClass(currentStepStatus: string): string {
    switch (currentStepStatus) {
      case CardStatus.DEFAULT:
        return 'bg-neutral-900'
      case CardStatus.RUNNING:
        return 'change-background-color pointer-events-none '
      case CardStatus.COMPLETED:
        return 'bg-green-800'
      default:
        return 'bg-neutral-900'
    }
  }
  getStepIconClass(currentStepStatus: string): string {
    switch (currentStepStatus) {
      case CardStatus.DEFAULT:
        return 'ph-circle-dashed text-neutral-400'
      case CardStatus.RUNNING:
        return 'ph-circle-dashed text-yellow-400 animate-spin'
      case CardStatus.COMPLETED:
        return 'ph-check text-green-200'
      default:
        return 'ph-circle-dashed text-neutral-400'
    }
  }

  getActionNameByAgent(agent: AiAgent): string {
    switch (agent) {
      case AiAgent.INTEGRATOR:
        return 'Integration(s)'
      case AiAgent.ARCHITECT:
        return 'Data Model'
      case AiAgent.ANALYST:
        return 'Business Process'
      case AiAgent.DESIGNER:
        return 'User Interface(s)'
      case AiAgent.DEVELOPER:
        return 'Business Rule(s)'
      default:
        return 'Unknown'
    }
  }
  getActionTypeByStatus(action: any): string {
    if (action.steps.every((step: any) => step.step_status === 'completed')) {
      return 'ph-check text-green-200'
    }
    return 'ph-circle-dashed text-neutral-400'
  }
  goToDestination(step: any): void {
    if (!step.step_destination?.routerParams) {
      return
    }
    // Derive workspace prefix from current URL (guard/workspace/:id)
    const currentUrl = this.#router.url
    const match = currentUrl.match(/^(\/guard\/workspace\/[^/]+)/)
    const prefix = match ? match[1] : ''
    const routerParams = step.step_destination.routerParams as string[]
    this.#router.navigateByUrl(`${prefix}/${routerParams.join('/')}`)
  }

  checkNodeExists(step: any): boolean {
    if (step?.step_destination?.type === 'Node') {
      return AiAnalystUtils.checkNodeExists(Number(step.step_destination.id))
    } else if (step?.step_destination?.type === 'Action') {
      return true
    } else if (step?.step_destination?.type === 'UI') {
      return true
    } else if (step?.step_destination?.type === 'DataModel') {
      return true
    } else if (step?.step_destination?.type === 'Project') {
      return String(step.step_destination.id) === String(this.currentAppId())
    } else if (step?.step_destination?.type === 'Process') {
      return String(step.step_destination.id) === String(this.currentProcess())
    }
    return true
  }

  getActionColorByStatus(action: any): string {
    if (action.steps.every((step: any) => step.step_status === 'completed')) {
      return 'bg-green-800'
    }
    return 'bg-neutral-900'
  }

  getStepTypeByStatus(step: any): string {
    if (step.step_status === 'completed') {
      return 'ph-check text-green-200'
    } else if (step.step_status === 'running') {
      return 'ph-circle-dashed text-yellow-400 animate-spin'
    }
    return 'ph-circle-dashed text-neutral-400'
  }

  getCanRunClass(step: any): string {
    if (!step.step_destination || this.streamingAiResponse() || this.hasDraft()) {
      return ' opacity-70'
    }
    return ' opacity-100'
  }

  getDisabledClass(step: any): string {
    if (
      (step.step_action === 'send_message' || step.step_action === 'receive_message') &&
      this.selectedPlan()
        .actions.find((action: any) => action.agent === 'integrator')
        ?.steps.find((step: any) => step.step_action === 'create_ai_agent')?.executionOutput ===
        null
    ) {
      return ' pointer-events-none bg-neutral-700 border-2 border-neutral-700 cursor-not-allowed text-neutral-400'
    }
    if (
      !step.step_destination ||
      this.streamingAiResponse() ||
      this.hasDraft() ||
      !this.checkNodeExists(step)
    ) {
      return ' pointer-events-none bg-neutral-700 border-2 border-neutral-700 cursor-not-allowed text-neutral-400'
    }

    if (step.step_status === 'completed' && step.step_destination) {
      return ' pointer-events-auto bg-green-900 hover:border-green-400 border-2 border-green-900 cursor-pointer text-green-500'
    }
    return ' pointer-events-auto bg-yellow-900 hover:border-yellow-400 border-2 border-yellow-900 cursor-pointer text-yellow-500'
  }

  getPlanDescription(): string {
    return this.selectedPlan()?.description || 'Here will be the description of the plan'
  }

  toggleStepDescription(step: any, agent: AiAgent): void {
    const newStep = { ...step, descriptionExpanded: !step.descriptionExpanded }
    this.#store.dispatch(AiActions.updateStep({ updateStep: newStep, agent }))
  }

  invokeStep(step: any, agent: AiAgent): void {
    this.#store.dispatch(AiActions.invokeStep({ selectedStep: step, agent }))
    this.#store.dispatch(AiCoordinatorActions.stepInvoked())
  }

  deletePlan(): void {
    this.#store.dispatch(AiActions.deletePlan())
  }

  onPromptClick(step: any, agent: AiAgent): void {
    if (step.recommended_prompt) {
      this.showModal(step)
    } else {
      this.#store.dispatch(AiCoordinatorActions.generatePromptForStep({ step, agent }))
    }
  }

  showModal(step: any): void {
    this.currentPrompt = step.recommended_prompt?.prompt ?? ''
    this.previewStep = step
    this.showPromptModal = true
  }
  closeModal(): void {
    this.showPromptModal = false
    this.currentPrompt = ''
  }

  copyPrompt(): void {
    navigator.clipboard.writeText(this.currentPrompt)
  }

  refineList(): void {
    this.#store.dispatch(AiActions.refineList())
  }

  automateProcess(): void {
    this.#store.dispatch(AiActions.automateProcess())
  }
}
